import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Landing from "@/pages/Landing";
import Convert from "@/pages/Convert";
import Repair from "@/pages/Repair";
import Split from "@/pages/Split";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ResetPassword from "@/pages/ResetPassword";
import Showcase from "@/pages/Showcase";
import HowTo from "@/pages/HowTo";
import Pricing from "@/pages/Pricing";
import Dashboard from "@/pages/Dashboard";
import CheckoutCallback from "@/pages/CheckoutCallback";
import AdminBilling from "@/pages/AdminBilling";
import Bulk from "@/pages/Bulk";
import Contact from "@/pages/Contact";
import FAQs from "@/pages/FAQs";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import ImpersonationBanner from "@/components/ImpersonationBanner";
import IncognitoModal from "@/components/IncognitoModal";

function Protected({ children }) {
  const { user, booted } = useAuth();
  const location = useLocation();
  if (!booted) {
    return (
      <div className="flex items-center justify-center h-[60vh] text-[var(--fg-2)] font-mono text-xs tracking-widest" data-testid="auth-loading">
        LOADING…
      </div>
    );
  }
  if (user === false || !user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  return children;
}

function RouteTracker() {
  const location = useLocation();
  const { user } = useAuth();
  const lastPath = useRef(null);
  useEffect(() => {
    const path = location.pathname;
    if (path === lastPath.current) return;
    lastPath.current = path;
    // Never count the admin console or the owner's own browsing.
    if (path.startsWith("/admin")) return;
    if (user && user.role === "admin") return;
    api.post("/track/pageview", { path }).catch(() => {});
  }, [location.pathname, user]);
  return null;
}

function Shell({ children }) {
  return (
    <div className="App min-h-screen flex flex-col">
      <RouteTracker />
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
      <ImpersonationBanner />
      <IncognitoModal />
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Toaster position="top-center" richColors theme="light" />
        <Shell>
          <Routes>
            <Route path="/" element={<Landing />} />
            <Route path="/convert" element={<Convert />} />
            <Route path="/repair" element={<Repair />} />
            <Route path="/split" element={<Split />} />
            <Route path="/how-to" element={<HowTo />} />
            <Route path="/pricing" element={<Pricing />} />
            <Route path="/checkout/callback" element={<CheckoutCallback />} />
            <Route
              path="/bulk"
              element={
                <Protected>
                  <Bulk />
                </Protected>
              }
            />
            <Route
              path="/dashboard"
              element={
                <Protected>
                  <Dashboard />
                </Protected>
              }
            />
            <Route
              path="/billing"
              element={<Navigate to="/dashboard?tab=billing" replace />}
            />
            <Route
              path="/admin/billing"
              element={
                <Protected>
                  <AdminBilling />
                </Protected>
              }
            />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/faqs" element={<FAQs />} />
            <Route path="/faq" element={<Navigate to="/faqs" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route
              path="/showcase"
              element={
                <Protected>
                  <Showcase />
                </Protected>
              }
            />
            <Route path="/history" element={<Navigate to="/convert" replace />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Shell>
      </AuthProvider>
    </BrowserRouter>
  );
}
